import { NgTemplateOutlet } from '@angular/common';
import type { InjectionToken } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, race, take, tap } from 'rxjs';

import { APP_CONFIG } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { TellentAppsActions } from '@core/tellent-data';
import type { SeatKind, TellentAdminAppConfig } from '@core/tellent-types';
import { ORG_GUID } from '@core/tellent-types';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  IconComponent,
  NotificationsController,
  SpinnerComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { StartFreeTrialDialogChipsComponent } from './start-free-trial-dialog-chips/start-free-trial-dialog-chips.component';
import { StartFreeTrialDialogSampleDataComponent } from './start-free-trial-dialog-sample-data/start-free-trial-dialog-sample-data.component';
import { StartFreeTrialDialogSuccessComponent } from './start-free-trial-dialog-success/start-free-trial-dialog-success.component';

type View = 'config' | 'pending' | 'success';

@Component({
  selector: 'tla-start-free-trial-dialog-lib',
  styleUrls: ['./start-free-trial-dialog.component.less'],
  templateUrl: './start-free-trial-dialog.component.html',
  host: {
    '[class]': 'brand()',
  },
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    ButtonComponent,
    NgTemplateOutlet,
    SpinnerComponent,
    StartFreeTrialDialogSampleDataComponent,
    StartFreeTrialDialogChipsComponent,
    StartFreeTrialDialogSuccessComponent,
    TranslatePipe,
    IconComponent,
  ],
})
export class StartFreeTrialDialogComponent implements DialogComponentI<
  { brand: 'kiwi' | 'recruitee'; seatKind: SeatKind },
  any
> {
  private readonly i18n = inject(I18nService);
  public readonly involvements = [
    {
      value: 'only_me',
      label: this.i18n.translate(
        'tl.dashboard.start_trial_dialog.config.involvements.options.only_me',
      ),
    },
    {
      value: 'team',
      label: this.i18n.translate(
        'tl.dashboard.start_trial_dialog.config.involvements.options.team',
      ),
    },
    {
      value: 'few_teams',
      label: this.i18n.translate(
        'tl.dashboard.start_trial_dialog.config.involvements.options.few_teams',
      ),
    },
    {
      value: 'recruitment_agency',
      label: this.i18n.translate(
        'tl.dashboard.start_trial_dialog.config.involvements.options.recruitment_agency',
      ),
    },
    {
      value: 'none',
      label: this.i18n.translate(
        'tl.dashboard.start_trial_dialog.config.involvements.options.none',
      ),
    },
  ];

  public brand = signal<'kiwi' | 'recruitee'>('recruitee');
  public seatKind = signal<SeatKind | null>(null);
  public view = signal<View>('config');
  public legalTerms = signal<string>('');

  public configTemplate = viewChild('config', { read: TemplateRef });
  public pendingTemplate = viewChild('pending', { read: TemplateRef });
  public successTemplate = viewChild('success', { read: TemplateRef });

  public dialogRef = inject(DialogRef);

  public viewTemplate = computed(() => {
    switch (this.view()) {
      case 'config':
        return this.configTemplate();
      case 'pending':
        return this.pendingTemplate();
      case 'success':
        return this.successTemplate();
      default:
        return this.configTemplate();
    }
  });

  public legalTermsTranslations: Record<'kiwi' | 'recruitee', string> = {
    kiwi: this.i18n.translate('tlc.org.add_dialog.apps.kiwi.terms_and_conditions'),
    recruitee: this.i18n.translate('tlc.org.add_dialog.apps.recruitee.terms_and_conditions'),
  };

  public form: FormGroup<{
    involvement: FormControl<string | null>;
    sampleData: FormControl<boolean | null>;
  }>;

  public showInvolvements = computed(() => this.brand() === 'recruitee');

  public successTexts = computed(() => {
    // eslint-disable-next-line default-case
    switch (this.brand()) {
      case 'recruitee':
        return {
          heading: this.i18n.translate('tl.dashboard.start_trial_dialog.success.recruitee.heading'),
          description: this.i18n.translate(
            'tl.dashboard.start_trial_dialog.success.recruitee.description',
          ),
          button: this.i18n.translate('tl.dashboard.start_trial_dialog.success.recruitee.button'),
        };
      case 'kiwi':
        return {
          heading: this.i18n.translate('tl.dashboard.start_trial_dialog.success.kiwi.heading'),
          description: this.i18n.translate(
            'tl.dashboard.start_trial_dialog.success.kiwi.description',
          ),
          button: this.i18n.translate('tl.dashboard.start_trial_dialog.success.kiwi.button'),
        };
    }
  });

  private store = inject(Store);
  private actions$ = inject(Actions);
  private destroyRef = inject(DestroyRef);
  private notifsCtrl = inject(NotificationsController);

  private appConfig: TellentAdminAppConfig = inject(
    <InjectionToken<TellentAdminAppConfig>>APP_CONFIG,
  );

  private windowRef = inject(WindowRef);
  private orgGuid = inject(ORG_GUID);

  public setDialogData(data: { brand: 'kiwi' | 'recruitee'; seatKind: SeatKind }) {
    this.seatKind.set(data.seatKind);
    this.brand.set(data.brand);
    this.legalTerms.set(this.legalTermsTranslations[data.brand]);
    this.form = new FormGroup({
      involvement: new FormControl<string | null>(null, [
        data.brand === 'recruitee' ? Validators.required : Validators.nullValidator,
      ]),
      sampleData: new FormControl<boolean | null>(null, [Validators.required]),
    });
  }

  public startTrial() {
    this.view.set('pending');

    const { involvement } = this.form.value;

    this.store.dispatch(
      TellentAppsActions.createApp({
        payload: {
          kind: this.seatKind()!,
          sampleData: this.form.value.sampleData!,
          signUpInfo: involvement
            ? {
                recruitmentInvolvement: this.form.value.involvement!,
              }
            : {},
        },
      }),
    );

    const ok$ = this.actions$.pipe(
      ofType(TellentAppsActions.createAppSuccess),
      map(() => true),
    );

    const fail$ = this.actions$.pipe(
      ofType(TellentAppsActions.createAppFailed),
      tap(({ payload }) => this.notifsCtrl.error(payload.error)),
      map(() => false),
    );

    race(ok$, fail$)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) {
          this.view.set('success');
        } else {
          this.dialogRef.close();
        }
      });
  }

  public explore() {
    this.windowRef.nativeWindow.location.href = `${
      this.appConfig.tellentAuthUrl
    }/switch?app_kind=${this.seatKind()}&org_guid=${this.orgGuid}`;
  }
}
